import { useAsyncList } from "@/utils/async/useAsyncList";
import type { IForexQuote } from "findef";
import {
  computed,
  onMounted,
  type ComputedRef,
  type Ref,
  ref,
  type MaybeRef,
  getCurrentScope,
} from "vue";
import { FindexClient } from "../..";
import { uref } from "@/utils/reactive/uref";
import { registerSharedCacheReset } from "@/utils/sharedCacheReset";

export type ForexTickerRef = Ref<IForexQuote>;

export type UseForexTickers = {
  tickers: Ref<IForexQuote[]>;
  getTickerRef: (
    tickerFrom: string,
    tickerTo: MaybeRef<string>,
  ) => ForexTickerRef;
  hasTicker: (tickerFrom: string, tickerTo: string) => boolean;
  refresh: () => void;
  convert: (value: number, tickerFrom: string, tickerTo: string) => number;
  loading: ComputedRef<boolean>;
};

const tickerMap: Record<
  string,
  Record<string, { ticker: ForexTickerRef; lastUpdate: Date | null }>
> = {};

const lookupTicker = (
  tickerFrom: string,
  tickerTo: string,
): { ticker: ForexTickerRef; lastUpdate: Date | null } => {
  if (!tickerMap[tickerFrom]) {
    tickerMap[tickerFrom] = {};
  }
  if (!tickerMap[tickerFrom][tickerTo]) {
    const x = 1;

    tickerMap[tickerFrom][tickerTo] = {
      lastUpdate: null,
      ticker: ref<IForexQuote>({
        ask: x,
        bid: x,
        changes: x,
        date: new Date(),
        high: x,
        low: x,
        open: x,
        tickerFrom,
        tickerTo,
      }),
    };
  }

  return tickerMap[tickerFrom][tickerTo];
};

const updateTicker = (
  tickerFrom: string,
  tickerTo: string,
  fun: (old: IForexQuote) => IForexQuote,
) => {
  const ticker = lookupTicker(tickerFrom, tickerTo);
  const now = new Date();
  const lastTime = ticker.lastUpdate;
  if (lastTime) {
    const diff = now.getTime() / 1000 - lastTime.getTime() / 1000;
    if (diff < 0.5) return;
  }
  const next = fun(ticker.ticker.value);
  if (JSON.stringify(next) === JSON.stringify(ticker.ticker.value)) return;

  tickerMap[tickerFrom][tickerTo].lastUpdate = now;
  ticker.ticker.value = next;
};

const listQuery = useAsyncList(
  async () => {
    const [items, res] = await FindexClient.getForexQuotes();
    if (res && res.error) throw res.error;
    const tickers = items || [];

    tickers.forEach((ticker) => {
      updateTicker(ticker.tickerFrom, ticker.tickerTo, () => ticker);
    });

    return tickers;
  },
  undefined,
  { immediate: false },
);

const getTime = () => new Date().getTime() / 1000;

let lastRefresh: number = -1;

let didInitialFetch: boolean = false;

// Drop the singleton state on logout so a re-login refetches /currency
// instead of reusing the previous session's quotes. FX quotes are public
// values, so this is freshness rather than privacy — but the entries in
// `tickerMap` are kept (and their refs reset in-place) so long-lived
// consumers that already obtained a ForexTickerRef across the logout
// boundary observe the new quote values when refresh() repopulates them.
// Dropping the map entries here would orphan those refs and the stale UI
// would never update.
//
// `bid` / `ask` reset to NaN, NOT 1, so a `convert()` call in the brief
// window between logout and the next /currency response surfaces as NaN
// in the UI instead of a silently-wrong 1:1 conversion (e.g. 100 USD
// rendering as 100 SEK).
registerSharedCacheReset(() => {
  didInitialFetch = false;
  lastRefresh = -1;
  for (const from of Object.keys(tickerMap)) {
    const inner = tickerMap[from];
    for (const to of Object.keys(inner)) {
      const entry = inner[to];
      entry.lastUpdate = null;
      entry.ticker.value = {
        ask: NaN, bid: NaN, changes: NaN, date: new Date(),
        high: NaN, low: NaN, open: NaN,
        tickerFrom: from, tickerTo: to,
      };
    }
  }
});

export const useForexTickers = (): UseForexTickers => {
  const tickers = computed(() => listQuery.items.value);

  const refresh = () => {
    const now = getTime();
    if (lastRefresh > 0) {
      const diff = now - lastRefresh;
      if (diff < 1.0) return;
    }

    lastRefresh = now;
    listQuery.refresh();
    didInitialFetch = true;
  };

  // Dedup barrier is the `!didInitialFetch` check INSIDE onMounted, not
  // out here — multiple consumers each register their own onMounted, but
  // only the first to run flips the flag. The outer `getCurrentScope()`
  // is just verifying onMounted is legal to call from this context (a
  // non-component caller would throw). The lastRefresh debounce inside
  // refresh() is a belt-and-braces guard against explicit refresh() calls
  // within 1 second.
  if (getCurrentScope()) {
    onMounted(() => {
      if (didInitialFetch) return;
      refresh();
    });
  }

  const getTicker = (tickerFrom: string, tickerTo: string) => {
    return tickers.value.find(
      (it) => it.tickerFrom === tickerFrom && it.tickerTo === tickerTo,
    );
  };

  const getTickerRef = (tickerFrom: string, tickerTo: MaybeRef<string>) => {
    if (!didInitialFetch) {
      refresh();
    }
    return lookupTicker(tickerFrom, uref(tickerTo)).ticker;
  };

  const hasTicker = (tickerFrom: string, tickerTo: string) => {
    return !!getTicker(tickerFrom, tickerTo);
  };

  const convert = (
    value: number,
    tickerFrom: string,
    tickerTo: string,
  ): number => {
    if (tickerFrom === tickerTo) return value;

    const ticker = getTicker(tickerFrom, tickerTo);
    if (!ticker) {
      console.error(
        `Unable to convert from ${tickerFrom} to ${tickerTo}. (Ticker not found)`,
      );
      return value;
    }
    return value * ticker.bid;
  };

  const loading = computed(() => {
    return listQuery.loading.value;
  });

  return { tickers, refresh, convert, hasTicker, loading, getTickerRef };
};
